<script>
  import { onMount, tick, setContext } from "svelte";
  import { fade } from "svelte/transition";
  import { tweened } from "svelte/motion";
  import { cubicInOut } from "svelte/easing";
  import throttle from "lodash.throttle";
  import { groups, sum } from "d3";
  import Meta from "./Meta.svelte";
  import Intro from "./Intro.svelte";
  import Universe from "./Universe.svelte";
  import AudioBg from "./Audio.Bg.svelte";
  import AudioAd from "./Audio.Ad.svelte";
  import Tip from "./Tip.svelte";
  import UI from "./UI.svelte";
  // import Analytics from "./Analytics.Body.svelte";
  import Drift from "./Drift.svelte";

  import {
    mode,
    progress,
    categoryIndex,
    activePlanet,
  } from "../stores/global.js";
  import viewport from "../stores/viewport.js";
  import fx from "../utils/fx.js";
  import showData from "../data/shows.csv";

  const INTRO_DURATION = 3500;
  const MIN_SHOWS_PER_CAT = 10;

  let appEl;
  let mounted = false;
  let ticking = false;
  let lastScrollY = 0;
  let scrollY = 0;
  let pointerX = 0;
  let pointerY = 0;
  let click;
  let audioStart;

  const scrollTween = tweened(0);

  const groupedCats = groups(showData, (d) => d.category);

  const catCounts = groupedCats.filter((d) => d[1].length >= MIN_SHOWS_PER_CAT);

  const catOtherCount = sum(
    groupedCats
      .filter((d) => d[1].length < MIN_SHOWS_PER_CAT)
      .map((d) => d[1].length)
  );

  catCounts.sort((a, b) => b[1].length - a[1].length);

  let offset = 0;
  const categories = catCounts
    .map((d) => {
      const o = {
        name: d[0],
        count: d[1].length,
        percent: d[1].length / showData.length,
        offset: offset,
      };
      offset += d[1].length / showData.length;
      return o;
    })
    .concat([
      {
        name: "Other",
        count: catOtherCount,
        percent: catOtherCount / showData.length,
        offset: offset,
      },
    ]);

  const shows = showData.map((d, index) => ({
    index,
    ...d,
    level: categories.map((c) => c.name).includes(d.category)
      ? d.category
      : "Other",
    slug: d.title.toLowerCase().replace(/\W/g, ""),
  }));

  setContext("App", { categories, shows });

  const updateScrollY = () => {
    scrollY = lastScrollY;
    ticking = false;
  };

  const onScroll = () => {
    lastScrollY = appEl.scrollTop;
    if (!ticking) {
      requestAnimationFrame(updateScrollY);
      ticking = true;
    }
  };

  const onMouseMove = (event) => {
    pointerX = (event.clientX / $viewport.width) * 2 - 1;
    pointerY = -(event.clientY / $viewport.height) * 2 + 1;
  };

  const onClick = (event) => {
    pointerX = (event.clientX / $viewport.width) * 2 - 1;
    pointerY = -(event.clientY / $viewport.height) * 2 + 1;
    click();
  };

  const onStart = () => {
    audioStart();
    $mode = "explore";
    scrollTween.set(0, {
      delay: 0,
      duration: INTRO_DURATION,
      easing: cubicInOut,
    });
  };

  const restart = async () => {
    const y = Math.floor(scrollHeight * 0.85);
    appEl.scrollTop = y;
    scrollTween.set(y, { duration: 0 });
    await tick();
    scrollTween.set(y / 2, { duration: INTRO_DURATION * 30 });
  };

  $: scrollHeight = $viewport.height * 10;
  $: $progress = scrollY / (scrollHeight - $viewport.height);
  $: if (mounted) appEl.scrollTop = $scrollTween;
  $: if ($progress <= 0.5 && $mode === "loaded") restart();

  onMount(() => {
    console.log("v3");
    mounted = true;
    restart();
  });
</script>

<!-- <Analytics /> -->
<Drift />
<Meta />
<AudioBg bind:start="{audioStart}" />
<AudioAd />

<div class="wrapper" style="--theme: var(--theme-{$categoryIndex});">
  <div
    id="app"
    style="height: {$viewport.height}px;"
    class:pointing="{!!$activePlanet}"
    bind:this="{appEl}"
    on:scroll="{onScroll}"
    on:mousemove="{throttle(onMouseMove, 50)}"
    on:click="{onClick}"
  >
    <div id="scroll" style="height: {scrollHeight}px;"></div>
  </div>

  <div id="universe">
    <Universe pointerX="{pointerX}" pointerY="{pointerY}" bind:click />
  </div>

  <Intro on:start="{onStart}" />

  <UI delay="{INTRO_DURATION}" />

  <Tip />
</div>

<style>
  #app {
    position: relative;
    width: 100%;
    min-height: 80vh;
    overflow-y: scroll;
    overflow-x: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  #app::-webkit-scrollbar {
    display: none;
  }

  #app.pointing {
    cursor: pointer;
  }

  #scroll {
    pointer-events: none;
  }
</style>
